import React, {
  memo, useMemo, useRef, Fragment,
} from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStream, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import {
  Box, IconButton, ListItemButton, ListItemIcon, Typography,
} from '@mui/material';
import itemTypes from '../itemTypes';
import { subGroupPropType } from '../../hooks';

function SKDGroupItem({
  subgroups, onClick, active, name, onDblClick, onHover, padded, onSubGroupClick, onChangeHierarchy,
  onCtxMenu,
}) {
  const ref = useRef(null);
  const [{ isOver }, drop] = useDrop({
    accept: [itemTypes.availableField, itemTypes.group],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true }),
    }),
    hover: (item, monitor) => {
      if (ref.current) {
        const gbcr = ref.current.getBoundingClientRect();
        const cursor = monitor.getClientOffset();
        if ((gbcr.top + gbcr.height / 2) > cursor.y) {
          onHover(name);
        }
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: itemTypes.group,
    item: { name, type: itemTypes.group },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const dragProps = useMemo(() => ({
    opacity: isDragging ? 0.4 : 1,
  }), [isDragging]);

  const btnProps = useMemo(() => ({
    pt: padded ? 3 : 0.5,
    transition: 'padding 0.3s ease-out',
    bgcolor: isOver ? 'grey.100' : null,
  }), [isOver, padded]);

  drop(ref);
  return (
    <ListItemButton
      sx={btnProps}
      selected={active}
      onClick={() => onClick(name)}
      onDoubleClick={() => onDblClick(name)}
      ref={ref}
      onContextMenu={(e) => onCtxMenu(e, ref.current)}
      data-name={name}
    >
      <ListItemIcon sx={{
        minWidth: 30, width: 30,
      }}
      />
      <Box display="flex" alignItems="center" ref={drag} sx={dragProps}>
        {subgroups.map((f) => (
          <Fragment key={f.name}>
            <Typography color={f.errored ? 'error' : 'text.primary'} variant="body2">
              {f.label}
            </Typography>
            <IconButton
              size="small"
            // variant="light"
              onClick={() => onChangeHierarchy(name, f.name, !f.useHierarchy)}
              disabled={!f.canHierarchy}
            >
              <FontAwesomeIcon
                icon={f.useHierarchy ? faStream : faBars}
                title={f.useHierarchy ? 'З ієрархія' : 'Без ієрархії'}
                color="var(--activeColorTextTab)"
              />
            </IconButton>
            { subgroups.length > 1 && (
              <IconButton
                size="small"
                onClick={() => onSubGroupClick(name, f.name)}
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  title="Прибрати"
                  transform="shrink-6 up-6 left-8"
                  color="#b90c0c"
                />
              </IconButton>
            )}
          </Fragment>
        ))}
      </Box>
    </ListItemButton>
  );
}

SKDGroupItem.propTypes = {
  name: PropTypes.string.isRequired,
  subgroups: PropTypes.arrayOf(subGroupPropType).isRequired,
  onClick: PropTypes.func.isRequired,
  onSubGroupClick: PropTypes.func.isRequired,
  onDblClick: PropTypes.func.isRequired,
  active: PropTypes.bool,
  onHover: PropTypes.func.isRequired,
  padded: PropTypes.bool,
  onChangeHierarchy: PropTypes.func.isRequired,
  onCtxMenu: PropTypes.func.isRequired,
};

SKDGroupItem.defaultProps = {
  active: false,
  padded: false,
};

export default memo(SKDGroupItem);
